import React from 'react';
import {
  FDBodyText,
  FDPaper,
  FDContentContainer,
  FDHeadlineText,
  FDBox,
  FDStack,
} from '@facet-wealth/design';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import PropTypes from 'prop-types';
import { useDesiredOutcomeContext } from '../../../../Context';

const DesiredOutcomesDetailItem = ({ label, value }) => {
  return (
    <FDBox paddingY={2}>
      <FDStack direction="column">
        <FDBodyText value={label} size={'md'} />
        <FDBodyText value={value} size={'sm'} />
      </FDStack>
    </FDBox>
  );
};

DesiredOutcomesDetailItem.defaultProps = {};
DesiredOutcomesDetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const cardListMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDBodyText,
  }, overrideReadOnly)
  .nameOverride(['value'], {
    _component: () => DesiredOutcomesDetailItem,
  }, overrideReadOnly);

const DesiredOutcomesDetailsLayout = () => {
  return (
    <MetaControlField attr="value" />
  );
};

const DesiredOutcomesCardListLayout = () => {
  const { resource } = useMetaLayoutContext();

  return (
    <FDStack direction="column" spacing={2}>
      <FDPaper padding={2}>
        {resource.attr('title') !== '' && (
        <MetaControlField attr="title" />
        )}
        <FDBox padding={2}>
          {resource.attr('summaryValues').resources().list().map(summaryValueResource => {
            return (
              <MetaFormInline
                padding="none"
                noIndividualSubmitButtons
                resource={summaryValueResource}
                layout={DesiredOutcomesDetailsLayout}
                metaOverrideSet={cardListMetaOverrideSet}
              />
            );
          })}
          {resource.attr('detailValues').resources().list().map(detailResource => {
            return (
              <MetaFormInline
                padding="none"
                noIndividualSubmitButtons
                resource={detailResource}
                layout={DesiredOutcomesDetailsLayout}
                metaOverrideSet={cardListMetaOverrideSet}
              />
            );
          })}
        </FDBox>
      </FDPaper>
    </FDStack>
  );
};

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
    _customProps: { size: 'sm' },
  }, {
    ordinal: 201,
  })
  .nameOverride(['subtitle'], {
    _component: () => FDBodyText,
  }, {
    ordinal: 201,
  });

const DesiredOutcomesSectionLayout = () => {
  const { resource } = useMetaLayoutContext();

  const outcomeSummaryDetailsCollection = resource
    .attr('outcomeSummaryDetails')
    .resources()
    .list();

  if (outcomeSummaryDetailsCollection.length === 0) {
    return <FDHeadlineText value="No Desired Outcomes" size={'sm'} />;
  }

  return (
    <FDStack direction="column" spacing={1}>
      <MetaControlField attr="title" />
      <MetaControlField attr="subtitle" />
      <FDStack direction="column" spacing={2}>
        {outcomeSummaryDetailsCollection.map(outcomeSummaryDetailsResource => {
          return (
            <MetaFormInline
              padding="none"
              noIndividualSubmitButtons
              resource={outcomeSummaryDetailsResource}
              layout={DesiredOutcomesCardListLayout}
              metaOverrideSet={cardListMetaOverrideSet}
            />);
        })}
      </FDStack>
    </FDStack>
  );
};

const DesiredOutcomesSection = () => {
  const { desiredOutcomeCollection } = useDesiredOutcomeContext();

  const resource = desiredOutcomeCollection.resources().head();
  if (!resource) {
    return null;
  }

  return (
    <FDContentContainer maxWidth={'sm'}>
      <MetaFormInline
        padding="none"
        noIndividualSubmitButtons
        resource={resource}
        layout={DesiredOutcomesSectionLayout}
        metaOverrideSet={metaOverrideSet}
      />
    </FDContentContainer>
  );
};

DesiredOutcomesSection.defaultProps = {};
DesiredOutcomesSection.propTypes = {};

export default DesiredOutcomesSection;
